import React, { useEffect, useState, useMemo } from 'react'
import { Select, Table, Card, Typography, Space, Button } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import {
  getAllCourses,
  getCourseCertificates
} from '../../reducers/courseReducer'
import * as XLSX from 'xlsx'

const { Option } = Select
const { Title } = Typography

const CourseCertificate = () => {
  const dispatch = useDispatch()
  const [selectedCourseId, setSelectedCourseId] = useState(null)

  const coursesData = useSelector((state) => state?.courses?.data)
  const currentUser = useSelector((state) => state?.auth?.user)
  const userRole = currentUser?.role

  const courses = useMemo(() => {
    if (!Array.isArray(coursesData)) return []
    return coursesData.filter((c) => c.status !== 'archived')
  }, [coursesData])

  const certificates = useMemo(() => {
    if (!selectedCourseId) return []

    const selectedCourse = courses.find(
      (course) => course.id === selectedCourseId
    )

    if (!selectedCourse) return []

    if (userRole === 'student') {
      const e = selectedCourse.myEnrollment

      if (!e?.certificateUrl || !e?.isCompleted || !e?.completedAt) {
        return []
      }

      return [
        {
          _id: currentUser.id,
          name: currentUser.name,
          certificateUrl: e.certificateUrl,
          completedAt: e.completedAt
        }
      ]
    }

    if (!Array.isArray(selectedCourse.enrollments)) return []

    return selectedCourse.enrollments
      .filter(
        (e) => e.certificateUrl && e.isCompleted === true && e.completedAt
      )
      .map((e) => ({
        _id: e.user.id,
        name: e.user.name,
        certificateUrl: e.certificateUrl,
        completedAt: e.completedAt
      }))
  }, [selectedCourseId, courses, userRole, currentUser])

  useEffect(() => {
    dispatch(getAllCourses('published'))
  }, [dispatch])

  useEffect(() => {
    if (courses.length > 0 && selectedCourseId === null) {
      setSelectedCourseId(courses[0].id)
    }
  }, [courses, selectedCourseId])

  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId || null)
  }

  const handleDownloadExcel = () => {
    if (!selectedCourseId || certificates.length === 0) return

    const selectedCourse = courses.find((c) => c.id === selectedCourseId)

    if (!selectedCourse) return

    const excelData = certificates.map((c) => ({
      'User Name': c.name,
      'Certificate URL': c.certificateUrl,
      'Completed On': c.completedAt
        ? new Date(c.completedAt).toLocaleDateString()
        : ''
    }))

    const worksheet = XLSX.utils.json_to_sheet(excelData)
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates')

    const safeFileName = selectedCourse.name
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()

    XLSX.writeFile(workbook, `${safeFileName}_certificates.xlsx`)
  }

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'name'
    },
    {
      title: 'Certificate',
      dataIndex: 'certificateUrl',
      render: (url) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          href={url}
          target="_blank"
        >
          View
        </Button>
      )
    },
    {
      title: 'Completed On',
      dataIndex: 'completedAt',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—')
    }
  ]

  return (
    <Card style={{ margin: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Title level={4}>Course Certificates</Title>

          {userRole != 'Student' && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!certificates.length}
              onClick={handleDownloadExcel}
            >
              Download Excel
            </Button>
          )}
        </div>

        <Select
          placeholder="Select Course"
          style={{ width: 300 }}
          value={selectedCourseId}
          onChange={handleCourseChange}
          allowClear
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {courses.map((course) => (
            <Option key={course.id} value={course.id}>
              {course.name}
            </Option>
          ))}
        </Select>

        <Table
          columns={columns}
          dataSource={certificates}
          rowKey="_id"
          pagination={{ pageSize: 5 }}
          bordered
          locale={{
            emptyText: selectedCourseId
              ? 'No certificates found for this course'
              : 'Please select a course to view certificates'
          }}
        />
      </Space>
    </Card>
  )
}

export default CourseCertificate
